import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import {
  canManageAdminWorkspace,
  createAdminUser,
  getAdminUser,
  getAdminUsers,
  isRoleElevationBlocked,
} from '../../lib/services/adminUsers'
import type { AdminUserSummary } from '../../lib/services/adminUsers'
import {
  ALL_CAPABILITIES,
  CAPABILITY_LABELS,
  ROLE_DEFAULT_CAPABILITIES,
  grantCapability,
  listCapabilities,
  revokeCapability,
  type Capability,
  type CapabilityGrant,
} from '../../lib/services/capabilities'
import { ChevronDown, ChevronUp, Mail, Plus, Shield, UserPlus, X } from 'lucide-react'
import { InviteFieldSurveyorModal } from './InviteFieldSurveyor'

type UserRole = 'superadmin' | 'admin' | 'editor' | 'user'

function UserCreateForm({ viewerRole, isSuperadmin, onCreated }: {
  viewerRole: 'Staff' | 'Surveyor' | 'Admin' | 'Reviewer' | 'FieldSurveyor'
  isSuperadmin: boolean
  onCreated: () => void
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<UserRole>('user')
  const [password, setPassword] = useState('')
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: createAdminUser,
    onSuccess: async () => {
      setEmail('')
      setRole('user')
      setPassword('')
      await queryClient.invalidateQueries({ queryKey: ['user-management-users'] })
      onCreated()
    },
  })

  const createRoleBlocked = !isSuperadmin && isRoleElevationBlocked(viewerRole, 'user', role)

  return (
    <form
      className="mt-4 space-y-3"
      onSubmit={async (e) => {
        e.preventDefault()
        await mutation.mutateAsync({
          email: email.trim(),
          role,
          temporary_password: password,
        })
      }}
    >
      <div className="grid gap-3 md:grid-cols-3">
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="email@example.com"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <select
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
        >
          {(['user', 'editor', 'admin', 'superadmin'] as const).map((r) => {
            const blocked = !isSuperadmin && isRoleElevationBlocked(viewerRole, 'user', r)
            return (
              <option key={r} value={r} disabled={blocked}>{r}</option>
            )
          })}
        </select>
        <input
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="Temporary password"
          type="password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={mutation.isPending || createRoleBlocked}
        >
          {mutation.isPending ? 'Creating...' : 'Create User'}
        </button>
        {createRoleBlocked && (
          <p className="text-xs text-slate-500">Unauthorized role elevation blocked.</p>
        )}
        {mutation.error && (
          <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
        )}
      </div>
    </form>
  )
}

function CapabilityToggles({ userId, userRole, facilityId }: {
  userId: string
  userRole: string
  facilityId: string
}) {
  const [grants, setGrants] = useState<CapabilityGrant[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [toggling, setToggling] = useState<string | null>(null)

  const defaults = ROLE_DEFAULT_CAPABILITIES[userRole] ?? []
  const grantable = ALL_CAPABILITIES.filter(c => !defaults.includes(c))

  useState(() => {
    listCapabilities(facilityId, userId)
      .then(setGrants)
      .catch(() => {})
      .finally(() => setIsLoading(false))
  })

  const handleToggle = async (cap: Capability) => {
    setToggling(cap)
    try {
      const existing = grants.find(g => g.capability === cap)
      if (existing) {
        await revokeCapability(existing.id)
        setGrants(prev => prev.filter(g => g.id !== existing.id))
      } else {
        const grant = await grantCapability(facilityId, userId, cap)
        setGrants(prev => [...prev, grant])
      }
    } catch {
      // Silently fail — user will see the toggle didn't change
    } finally {
      setToggling(null)
    }
  }

  if (isLoading) return <p className="text-xs text-slate-400">Loading capabilities...</p>
  if (grantable.length === 0) return <p className="text-xs text-slate-400">All capabilities included by role.</p>

  return (
    <div className="space-y-1.5">
      {grantable.map(cap => {
        const isGranted = grants.some(g => g.capability === cap)
        const isToggling = toggling === cap
        return (
          <label key={cap} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isGranted}
              disabled={isToggling}
              onChange={() => handleToggle(cap)}
              className="h-4 w-4 rounded border-slate-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
            />
            <span className={isToggling ? 'text-slate-400' : 'text-slate-700'}>{CAPABILITY_LABELS[cap]}</span>
          </label>
        )
      })}
    </div>
  )
}

function UserFacilityCard({ user }: { user: AdminUserSummary }) {
  const [expanded, setExpanded] = useState(false)
  const [facilities, setFacilities] = useState<{ facility_id: string; name: string }[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadFacilities = async () => {
    if (facilities !== null) return
    setIsLoading(true)
    setError(null)
    try {
      const detail = await getAdminUser(user.user_id)
      setFacilities(
        detail.facility_assignments.map((a) => {
          const match = detail.available_facilities.find((f) => f.facility_id === a.facility_id)
          return {
            facility_id: a.facility_id,
            name: match?.facility_name ?? a.facility_id,
          }
        }),
      )
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleExpand = async () => {
    const next = !expanded
    setExpanded(next)
    if (next) {
      await loadFacilities()
    }
  }

  return (
    <div className="v11-dashboard-card">
      <button
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={toggleExpand}
      >
        <div className="flex items-center gap-3">
          <Mail size={16} className="text-[var(--brand-primary)]" />
          <div>
            <p className="text-sm font-bold text-slate-900">{user.email}</p>
            <p className="text-xs text-slate-500">
              {user.first_name || user.last_name
                ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
                : 'No name set'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={user.role === 'admin' || user.role === 'superadmin' ? 'badge-released' : 'badge-in-progress'}>
            <Shield size={10} className="mr-1 inline" />
            {user.role}
          </span>
          {!user.active && (
            <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600">Inactive</span>
          )}
          {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Facility Assignments</p>

          {isLoading && <p className="mt-2 text-sm text-slate-500">Loading...</p>}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

          {!isLoading && facilities !== null && (
            <>
              {facilities.length === 0 && (
                <p className="mt-2 text-sm text-slate-500">No facilities assigned.</p>
              )}
              <div className="mt-2 grid gap-1">
                {facilities.map((f) => (
                  <div key={f.facility_id} className="space-y-2">
                    <div className="flex items-center gap-2 rounded bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
                      <span>{f.name}</span>
                    </div>
                    <div className="ml-4">
                      <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Delegated Capabilities</p>
                      <CapabilityToggles userId={user.user_id} userRole={user.role} facilityId={f.facility_id} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function UserManagementPage() {
  const { viewerContext } = useAuth()
  const role = viewerContext?.role || 'Surveyor'
  const canManage = canManageAdminWorkspace(role)
  const isSuperadmin = viewerContext?.is_superadmin === true
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showInviteSurveyor, setShowInviteSurveyor] = useState(false)
  const canInviteSurveyor = (viewerContext?.tier === 'consultant' || viewerContext?.tier === 'enterprise') && role === 'Admin'

  const usersQuery = useQuery({
    queryKey: ['user-management-users'],
    queryFn: getAdminUsers,
    enabled: canManage,
  })

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        User management is restricted to Admin role.
      </div>
    )
  }

  const users = usersQuery.data ?? []

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">User Management</h1>
            <p className="mt-1 text-sm text-slate-600">
              Create users and view their facility assignments.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canInviteSurveyor && (
              <button
                className="flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                onClick={() => setShowInviteSurveyor(true)}
              >
                <UserPlus size={14} />
                Invite Surveyor
              </button>
            )}
          <button
            className="btn-primary flex items-center gap-1.5 px-4 py-2 text-sm"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? (
              <>
                <X size={14} />
                Cancel
              </>
            ) : (
              <>
                <Plus size={14} />
                New User
              </>
            )}
          </button>
          </div>
        </div>
      </header>
      {showInviteSurveyor && <InviteFieldSurveyorModal onClose={() => setShowInviteSurveyor(false)} />}

      {showCreateForm && (
        <section className="v11-section p-5">
          <h2 className="section-title">Create User</h2>
          <UserCreateForm
            viewerRole={role}
            isSuperadmin={isSuperadmin}
            onCreated={() => setShowCreateForm(false)}
          />
        </section>
      )}

      <section className="v11-section p-5">
        <h2 className="section-title">Users</h2>

        {usersQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading users...</p>}
        {usersQuery.error && (
          <p className="mt-2 text-sm text-red-600">{(usersQuery.error as Error).message}</p>
        )}

        {!usersQuery.isLoading && !usersQuery.error && users.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No users found.</p>
        )}

        <div className="mt-3 grid gap-3">
          {users.map((user) => (
            <UserFacilityCard key={user.user_id} user={user} />
          ))}
        </div>
      </section>
    </div>
  )
}
